import { useState, useMemo, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useStorefront, useCrearPedidoPublico } from '@almacenes/core';
import { MdSearch, MdShoppingCart, MdStorefront } from 'react-icons/md';
import logoAD from '@/assets/logos/nuevo.svg';

import { toTitleCase, BRAND } from './utils';
import { useCarrito } from './hooks/use-carrito';
import { MarcaAD } from './components/MarcaAD';
import { PantallaExito } from './components/PantallaExito';
import { ProductoCard } from './components/ProductoCard';
import { ModalGranel } from './components/ModalGranel';
import { PanelCarrito } from './components/PanelCarrito';

const PAGE_SIZE = 24;

export const TiendaPage = () => {
  const { almacenId } = useParams<{ almacenId: string }>();
  const id = Number(almacenId);

  const { data, isLoading, isError } = useStorefront(id);
  const crearPedido = useCrearPedidoPublico(id);

  // Filtros y paginación
  const [busqueda, setBusqueda] = useState('');
  const [ordenamiento, setOrdenamiento] = useState<'nombre' | 'precio-asc' | 'precio-desc'>('nombre');
  const [categoriaActiva, setCategoriaActiva] = useState<string | null>(null);
  const [visibles, setVisibles] = useState(PAGE_SIZE);
  const [scrolled, setScrolled] = useState(false);
  const busquedaRef = useRef<HTMLInputElement>(null);

  // UI state
  const [carritoAbierto, setCarritoAbierto] = useState(false);
  const [pedidoExitoso, setPedidoExitoso] = useState<number | null>(null);
  const [modalGranel, setModalGranel] = useState<Parameters<typeof ModalGranel>[0]['producto'] | null>(null);

  // Carrito
  const {
    carrito, setCarrito, badgePulse, total, cantidadItems,
    agregarAlCarrito, cambiarCantidad, setCantidadGranel, eliminarDelCarrito, cantidadEnCarrito,
  } = useCarrito(id, data?.productos);

  // Efectos
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setVisibles(PAGE_SIZE); }, [busqueda, ordenamiento, categoriaActiva]);

  const categorias = useMemo(() => {
    if (!data) return [];
    return [...new Set(data.productos.map((p) => p.categoria).filter(Boolean))].sort((a, b) =>
      (a as string).localeCompare(b as string)
    ) as string[];
  }, [data]);

  const productosFiltrados = useMemo(() => {
    if (!data) return [];
    const q = busqueda.toLowerCase();
    let lista = data.productos.filter(
      (p) => p.nombre.toLowerCase().includes(q) && (categoriaActiva === null || p.categoria === categoriaActiva)
    );
    if (ordenamiento === 'precio-asc') lista = [...lista].sort((a, b) => a.precioVenta - b.precioVenta);
    else if (ordenamiento === 'precio-desc') lista = [...lista].sort((a, b) => b.precioVenta - a.precioVenta);
    else lista = [...lista].sort((a, b) => a.nombre.localeCompare(b.nombre));
    return lista;
  }, [data, busqueda, ordenamiento, categoriaActiva]);

  // ─── Estados de pantalla ────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 gap-6">
        <div className="flex flex-col items-center gap-1">
          <img src={logoAD} alt="" aria-hidden="true" className="h-8 mb-1 opacity-80" />
          <span className="text-xs font-bold tracking-widest" style={{ color: BRAND.navy }}>
            ALMACENES<span style={{ color: BRAND.cyan }}>digitales</span>
          </span>
        </div>
        <div className="flex gap-1.5" role="status" aria-label="Cargando tienda">
          {[0, 1, 2].map((i) => (
            <div key={i} className="w-2 h-2 rounded-full animate-bounce"
              style={{ backgroundColor: BRAND.cyan, animationDelay: `${i * 150}ms` }} />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 px-4 gap-4">
        <MdStorefront className="text-5xl text-gray-300" aria-hidden="true" />
        <div className="text-center">
          <h1 className="text-label text-gray-800 mb-1">Tienda no disponible</h1>
          <p className="text-muted">No pudimos cargar el catálogo. Intenta más tarde.</p>
        </div>
      </div>
    );
  }

  if (pedidoExitoso !== null) {
    return (
      <PantallaExito
        pedidoId={pedidoExitoso}
        onVolver={() => { setCarrito([]); setPedidoExitoso(null); }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <header
        className={`bg-white border-b border-gray-200 sticky top-0 z-20 transition-shadow duration-300${scrolled ? ' shadow-md' : ''}`}
      >
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {data.logoUrl ? (
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 overflow-hidden p-1 shadow-sm"
                style={{ backgroundColor: data.colorFondo || '#F3F4F9' }}
              >
                <img src={data.logoUrl} alt="" aria-hidden="true" className="w-full h-full object-contain" />
              </div>
            ) : (
              <MdStorefront className="shrink-0 text-2xl" style={{ color: BRAND.cyan }} aria-hidden="true" />
            )}
            <h1 className="font-bold text-base truncate" style={{ color: BRAND.navy }} title={data.nombre}>
              {data.nombre}
            </h1>
            <span className="hidden sm:inline text-gray-300 shrink-0">·</span>
            <div className="hidden sm:block shrink-0"><MarcaAD /></div>
          </div>

          <button
            onClick={() => setCarritoAbierto(true)}
            className="btn-primary relative flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm shrink-0 transition-transform active:scale-95"
            aria-label={`Abrir carrito, ${cantidadItems} ${cantidadItems === 1 ? 'producto' : 'productos'}`}
          >
            <MdShoppingCart className="text-lg" aria-hidden="true" />
            <span className="hidden sm:inline">Carrito</span>
            <span
              aria-live="polite"
              aria-atomic="true"
              aria-hidden={cantidadItems === 0}
              className={`absolute -top-1.5 -right-1.5 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-0.5 transition-transform${badgePulse ? ' scale-125' : ''} ${cantidadItems === 0 ? 'invisible' : ''}`}
              style={{ backgroundColor: '#EF4444' }}
            >
              {cantidadItems}
            </span>
          </button>
        </div>
      </header>

      {/* Filtros */}
      <div className="max-w-4xl mx-auto px-4 pt-4 pb-3 w-full space-y-2">
        {/* Búsqueda — siempre ancho completo */}
        <div className="relative">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input
            ref={busquedaRef}
            id="busqueda-producto"
            type="search"
            placeholder="Buscar producto…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            aria-label="Buscar producto"
            className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-2xl text-sm bg-white focus:outline-none focus:ring-2 shadow-sm"
            style={{ '--tw-ring-color': BRAND.cyan } as React.CSSProperties}
          />
        </div>
        {/* Categoría + Ordenamiento — lado a lado en móvil y desktop */}
        <div className="flex gap-2">
          {categorias.length > 0 && (
            <div className="relative flex-1 min-w-0">
              <select
                value={categoriaActiva ?? ''}
                onChange={(e) => setCategoriaActiva(e.target.value || null)}
                aria-label="Filtrar por categoría"
                className="w-full py-2.5 pl-3 pr-8 border border-gray-200 rounded-2xl text-sm bg-white focus:outline-none focus:ring-2 shadow-sm appearance-none truncate"
                style={{ '--tw-ring-color': BRAND.cyan, color: categoriaActiva ? BRAND.navy : '#9CA3AF' } as React.CSSProperties}
              >
                <option value="">Categoría</option>
                {categorias.map((cat) => <option key={cat} value={cat}>{toTitleCase(cat)}</option>)}
              </select>
              <ChevronIcon />
            </div>
          )}
          <div className="relative flex-1 min-w-0">
            <select
              value={ordenamiento}
              onChange={(e) => setOrdenamiento(e.target.value as typeof ordenamiento)}
              aria-label="Ordenar productos"
              className="w-full py-2.5 pl-3 pr-8 border border-gray-200 rounded-2xl text-sm bg-white focus:outline-none focus:ring-2 shadow-sm appearance-none"
              style={{ '--tw-ring-color': BRAND.cyan, color: BRAND.navy } as React.CSSProperties}
            >
              <option value="nombre">A–Z</option>
              <option value="precio-asc">Menor precio</option>
              <option value="precio-desc">Mayor precio</option>
            </select>
            <ChevronIcon />
          </div>
        </div>
      </div>

      {/* Catálogo */}
      <main id="catalogo" className="max-w-4xl mx-auto px-4 pb-8 w-full flex-1">
        {productosFiltrados.length === 0 ? (
          busqueda === '' && categoriaActiva === null ? (
            <p className="text-center py-16 text-muted" role="status">
              Este almacén no tiene productos disponibles aún.
            </p>
          ) : (
            <div className="flex flex-col items-center gap-3 py-16" role="status">
              <p className="text-muted">No hay productos que coincidan con los filtros aplicados.</p>
              <button
                onClick={() => { setBusqueda(''); setCategoriaActiva(null); busquedaRef.current?.focus(); }}
                className="text-sm font-semibold px-4 py-1.5 rounded-full border transition-colors"
                style={{ borderColor: BRAND.cyan, color: BRAND.cyan }}
              >
                Limpiar filtros
              </button>
            </div>
          )
        ) : (
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-2 list-none p-0 items-stretch" aria-label="Catálogo de productos">
            {productosFiltrados.slice(0, visibles).map((producto, idx) => (
              <li key={producto.productoAlmacenId} className="flex card-in" style={{ animationDelay: `${Math.min(idx, 8) * 40}ms` }}>
                <ProductoCard
                  producto={producto}
                  enCarrito={cantidadEnCarrito(producto.productoAlmacenId)}
                  onAgregar={() => {
                    if (producto.venderPor === 'F') setModalGranel(producto);
                    else agregarAlCarrito(producto, 1, producto.precioVenta);
                  }}
                  onCambiarCantidad={(delta) => cambiarCantidad(producto.productoAlmacenId, delta)}
                  onEditarGranel={() => setModalGranel(producto)}
                />
              </li>
            ))}
          </ul>
        )}

        {productosFiltrados.length > visibles && (
          <div className="flex justify-center mt-6 mb-2">
            <button
              onClick={() => setVisibles((v) => v + PAGE_SIZE)}
              className="px-6 py-2.5 rounded-2xl text-sm font-semibold border-2 transition-all duration-200 active:scale-95"
              style={{ borderColor: BRAND.cyan, color: BRAND.cyan, backgroundColor: '#fff' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = BRAND.cyan; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = BRAND.cyan; }}
            >
              Cargar más ({productosFiltrados.length - visibles} restantes)
            </button>
          </div>
        )}
      </main>

      <div className="sm:hidden flex justify-center py-4"><MarcaAD /></div>

      {modalGranel && (
        <ModalGranel
          producto={modalGranel}
          cantidadActual={cantidadEnCarrito(modalGranel.productoAlmacenId)}
          onConfirmar={(cantidad, subtotal) => {
            if (cantidadEnCarrito(modalGranel.productoAlmacenId) > 0)
              setCantidadGranel(modalGranel.productoAlmacenId, cantidad, subtotal);
            else
              agregarAlCarrito(modalGranel, cantidad, subtotal);
            setModalGranel(null);
          }}
          onCerrar={() => setModalGranel(null)}
        />
      )}

      {carritoAbierto && (
        <PanelCarrito
          carrito={carrito}
          total={total}
          almacenNombre={data.nombre}
          logoUrl={data.logoUrl}
          colorFondo={data.colorFondo}
          onCambiarCantidad={cambiarCantidad}
          onEliminarItem={eliminarDelCarrito}
          onEditarGranel={(producto) => { setCarritoAbierto(false); setModalGranel(producto); }}
          onCerrar={() => setCarritoAbierto(false)}
          onPedidoCreado={(pedidoId) => { setPedidoExitoso(pedidoId); setCarritoAbierto(false); }}
          crearPedido={crearPedido}
        />
      )}
    </div>
  );
};

// ─── Chevron reutilizable para selects ───────────────────────────────────────

const ChevronIcon = () => (
  <svg
    className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
    viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
  >
    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);
